import React, { useState } from 'react';
import { 
  ArrowLeft, Clock, Plus, Save, X, Calendar, AlertCircle, Check, Trash2, 
  ChevronDown, ChevronUp, Edit3, Copy
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { SPECIAL_HOURS_TYPES, getWorkingHoursByOfficeId } from '../data/database';

// Orari di lavoro di default
const DEFAULT_WORKING_HOURS = {
  monday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }, { start: '14:30', end: '19:00' }] },
  tuesday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }, { start: '14:30', end: '19:00' }] },
  wednesday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }, { start: '14:30', end: '19:00' }] },
  thursday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }, { start: '14:30', end: '19:00' }] },
  friday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }, { start: '14:30', end: '19:00' }] },
  saturday: { enabled: true, shifts: [{ start: '09:00', end: '13:00' }] },
  sunday: { enabled: false, shifts: [] },
};

const DAYS_OF_WEEK = [
  { id: 'monday', name: 'Lunedì', short: 'Lun' },
  { id: 'tuesday', name: 'Martedì', short: 'Mar' },
  { id: 'wednesday', name: 'Mercoledì', short: 'Mer' },
  { id: 'thursday', name: 'Giovedì', short: 'Gio' },
  { id: 'friday', name: 'Venerdì', short: 'Ven' },
  { id: 'saturday', name: 'Sabato', short: 'Sab' },
  { id: 'sunday', name: 'Domenica', short: 'Dom' },
];

const BusinessHoursPage = ({ onBack, onNavigate }) => {
  const { user, selectedOffice } = useApp();
  
  // Build working hours from business data or fallback to OFFICE_WORKING_HOURS
  const initHours = () => {
    if (selectedOffice?.hours) {
      // Convert business.hours array format to working hours state format
      const dayMap = {
        'Lunedi': 'monday', 'Martedi': 'tuesday', 'Mercoledi': 'wednesday',
        'Giovedi': 'thursday', 'Venerdi': 'friday', 'Sabato': 'saturday', 'Domenica': 'sunday'
      };
      const result = {};
      Object.entries(dayMap).forEach(([it, en]) => {
        const dayData = selectedOffice.hours.find(h => h.day === it);
        if (!dayData || dayData.hours === 'Chiuso') {
          result[en] = { enabled: false, shifts: [{ start: '09:00', end: '18:00' }] };
        } else {
          const shifts = dayData.hours.split(',').map(s => s.trim()).map(s => {
            const parts = s.split(' - ');
            return { start: parts[0]?.trim() || '09:00', end: parts[1]?.trim() || '18:00' };
          });
          result[en] = { enabled: true, shifts };
        }
      });
      return result;
    }
    return DEFAULT_WORKING_HOURS;
  };
  
  const [workingHours, setWorkingHours] = useState(initHours);
  
  // Special hours
  const [specialHours, setSpecialHours] = useState(OFFICE_WORKING_HOURS.specialDays || []);
  
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('standard');
  const [showSpecialModal, setShowSpecialModal] = useState(false);
  const [editingSpecial, setEditingSpecial] = useState(null);
  
  // Special hours form
  const [specialForm, setSpecialForm] = useState({
    type: 'closure',
    date: '',
    reason: '',
    allDay: true,
    shifts: [{ start: '09:00', end: '18:00' }],
  });

  // Toggle day enabled/disabled
  const toggleDay = (dayId) => {
    setWorkingHours(prev => ({
      ...prev,
      [dayId]: { ...prev[dayId], enabled: !prev[dayId].enabled }
    }));
    setHasChanges(true);
  };

  // Update shift time
  const updateShift = (dayId, shiftIndex, field, value) => {
    setWorkingHours(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        shifts: prev[dayId].shifts.map((shift, i) => 
          i === shiftIndex ? { ...shift, [field]: value } : shift
        )
      }
    }));
    setHasChanges(true);
  };

  // Add shift to a day
  const addShift = (dayId) => {
    setWorkingHours(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        shifts: [...prev[dayId].shifts, { start: '14:00', end: '18:00' }]
      }
    }));
    setHasChanges(true);
  };

  // Remove shift from a day
  const removeShift = (dayId, shiftIndex) => {
    setWorkingHours(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        shifts: prev[dayId].shifts.filter((_, i) => i !== shiftIndex)
      }
    }));
    setHasChanges(true);
  };

  // Copy hours to other days
  const copyToAllDays = (sourceDayId) => {
    const sourceHours = workingHours[sourceDayId];
    setWorkingHours(prev => {
      const newHours = { ...prev };
      DAYS_OF_WEEK.forEach(day => {
        if (day.id !== sourceDayId) {
          newHours[day.id] = { ...sourceHours };
        }
      });
      return newHours;
    });
    setHasChanges(true);
  };

  // Special hours CRUD
  const openAddSpecial = () => {
    setEditingSpecial(null);
    setSpecialForm({
      type: 'closure',
      date: '',
      reason: '',
      allDay: true,
      shifts: [{ start: '09:00', end: '18:00' }],
    });
    setShowSpecialModal(true);
  };

  const openEditSpecial = (special) => {
    setEditingSpecial(special);
    setSpecialForm({
      type: special.type,
      date: special.date,
      reason: special.reason,
      allDay: special.allDay || false,
      shifts: special.shifts || [{ start: '09:00', end: '18:00' }],
    });
    setShowSpecialModal(true);
  };

  const saveSpecial = () => {
    if (!specialForm.date || !specialForm.reason) return;
    
    const specialData = {
      id: editingSpecial?.id || Date.now(),
      type: specialForm.type,
      date: specialForm.date,
      reason: specialForm.reason,
      allDay: specialForm.type === 'closure' ? true : specialForm.allDay,
      shifts: specialForm.type === 'closure' ? [] : specialForm.shifts,
    };
    
    if (editingSpecial) {
      setSpecialHours(prev => prev.map(s => s.id === editingSpecial.id ? specialData : s));
    } else {
      setSpecialHours(prev => [...prev, specialData]);
    }
    
    setShowSpecialModal(false);
    setHasChanges(true);
  };

  const deleteSpecial = (id) => {
    setSpecialHours(prev => prev.filter(s => s.id !== id));
    setHasChanges(true);
  };

  const handleSave = () => {
    // In real app, would save to backend
    setHasChanges(false);
    alert('Orari salvati con successo!');
  };

  const formatShifts = (shifts) => {
    if (!shifts || shifts.length === 0) return 'Chiuso';
    return shifts.map(s => `${s.start} - ${s.end}`).join(', ');
  };

  const getSpecialTypeConfig = (type) => SPECIAL_HOURS_TYPES[type.toUpperCase()] || SPECIAL_HOURS_TYPES.CLOSURE;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack} 
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                data-testid="hours-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Gestione Orari</h1>
                <p className="text-sm text-gray-500">Imposta orari di lavoro e chiusure</p>
              </div>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
                data-testid="save-hours-btn"
              >
                <Save className="w-4 h-4" />
                Salva
              </button>
            )}
          </div>

          {/* Section Tabs */}
          <div className="flex gap-2 px-4 pb-3">
            <button
              onClick={() => setActiveSection('standard')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === 'standard' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Clock className="w-4 h-4" />
              Orari Standard
            </button>
            <button
              onClick={() => setActiveSection('special')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === 'special' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Giorni Speciali
              {specialHours.length > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeSection === 'special' ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {specialHours.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto p-4">
        {/* Standard Hours */}
        {activeSection === 'standard' && (
          <div className="space-y-3">
            {DAYS_OF_WEEK.map((day) => {
              const dayHours = workingHours[day.id];
              return (
                <div key={day.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleDay(day.id)}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                          dayHours.enabled 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {dayHours.enabled ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                      </button>
                      <div>
                        <p className="font-semibold text-gray-900">{day.name}</p>
                        <p className="text-sm text-gray-500">
                          {dayHours.enabled ? formatShifts(dayHours.shifts) : 'Chiuso'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {dayHours.enabled && (
                        <button
                          onClick={() => copyToAllDays(day.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Copia a tutti i giorni"
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Shifts Editor */}
                  {dayHours.enabled && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                      {dayHours.shifts.map((shift, shiftIndex) => (
                        <div key={shiftIndex} className="flex items-center gap-3">
                          <span className="text-sm text-gray-500 w-16">Turno {shiftIndex + 1}</span>
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="time"
                              value={shift.start}
                              onChange={(e) => updateShift(day.id, shiftIndex, 'start', e.target.value)}
                              className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                              type="time"
                              value={shift.end}
                              onChange={(e) => updateShift(day.id, shiftIndex, 'end', e.target.value)}
                              className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          {dayHours.shifts.length > 1 && (
                            <button
                              onClick={() => removeShift(day.id, shiftIndex)}
                              className="p-2 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => addShift(day.id)}
                        className="flex items-center gap-1 text-emerald-600 text-sm font-medium hover:text-emerald-700"
                      >
                        <Plus className="w-4 h-4" />
                        Aggiungi turno
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Special Hours */}
        {activeSection === 'special' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Gestisci chiusure straordinarie, aperture extra e festività
              </p>
              <button
                onClick={openAddSpecial}
                className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-600"
                data-testid="add-special-btn"
              >
                <Plus className="w-4 h-4" />
                Aggiungi
              </button>
            </div>

            {specialHours.length > 0 ? (
              <div className="space-y-3">
                {specialHours.sort((a, b) => a.date.localeCompare(b.date)).map((special) => {
                  const config = getSpecialTypeConfig(special.type);
                  return (
                    <div key={special.id} className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          special.type === 'closure' ? 'bg-red-100' :
                          special.type === 'extra_opening' ? 'bg-emerald-100' :
                          special.type === 'holiday' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          <Calendar className={`w-6 h-6 ${
                            special.type === 'closure' ? 'text-red-600' :
                            special.type === 'extra_opening' ? 'text-emerald-600' :
                            special.type === 'holiday' ? 'text-amber-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{special.reason}</h4>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              special.type === 'closure' ? 'bg-red-100 text-red-700' :
                              special.type === 'extra_opening' ? 'bg-emerald-100 text-emerald-700' :
                              special.type === 'holiday' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {config.name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(special.date).toLocaleDateString('it-IT', { 
                              weekday: 'long', 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </p>
                          {special.shifts && special.shifts.length > 0 && (
                            <p className="text-sm text-gray-500">
                              Orario: {formatShifts(special.shifts)}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditSpecial(special)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <Edit3 className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => deleteSpecial(special.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun giorno speciale</h3>
                <p className="text-gray-500 mb-4">Aggiungi chiusure, festività o aperture extra</p>
                <button
                  onClick={openAddSpecial}
                  className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600"
                >
                  <Plus className="w-5 h-5" />
                  Aggiungi giorno speciale
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Special Hours Modal */}
      {showSpecialModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {editingSpecial ? 'Modifica' : 'Nuovo'} giorno speciale
              </h3>
              <button onClick={() => setShowSpecialModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.values(SPECIAL_HOURS_TYPES).map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSpecialForm({ ...specialForm, type: type.id })}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        specialForm.type === type.id 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-gray-900 text-sm">{type.name}</p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                <input
                  type="date"
                  value={specialForm.date}
                  onChange={(e) => setSpecialForm({ ...specialForm, date: e.target.value })}
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
                <input
                  type="text"
                  value={specialForm.reason}
                  onChange={(e) => setSpecialForm({ ...specialForm, reason: e.target.value })}
                  placeholder="Es. Ferie estive, Evento speciale..."
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Hours (for non-closure types) */}
              {specialForm.type !== 'closure' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Orario</label>
                  {specialForm.shifts.map((shift, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <input
                        type="time"
                        value={shift.start}
                        onChange={(e) => {
                          const newShifts = [...specialForm.shifts];
                          newShifts[i] = { ...newShifts[i], start: e.target.value };
                          setSpecialForm({ ...specialForm, shifts: newShifts });
                        }}
                        className="flex-1 bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="time"
                        value={shift.end}
                        onChange={(e) => {
                          const newShifts = [...specialForm.shifts];
                          newShifts[i] = { ...newShifts[i], end: e.target.value };
                          setSpecialForm({ ...specialForm, shifts: newShifts });
                        }}
                        className="flex-1 bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={saveSpecial}
              disabled={!specialForm.date || !specialForm.reason}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 mt-6"
            >
              {editingSpecial ? 'Salva modifiche' : 'Aggiungi'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessHoursPage;
